import type { Color } from "@/model/baseTypes";
import color from "@/model/color.module.scss";

import { colorPalette } from "./colorPalette";
import { toHex } from "./string";

const black: Color = [0, 0, 0, 1];
export const white: Color = [1, 1, 1, 1];

export function toCssColor(c: Color): string {
  const hex = (v: number) => toHex(Math.round(v), 2);
  return "#" + hex(c[0] * 255) + hex(c[1] * 255) + hex(c[2] * 255) + hex(c[3] * 255);
}

/**
 * Converts a hexadecimal color code to an RGB color representation.
 *
 * @param hex - The hexadecimal color code to convert.
 * @returns The RGB color representation as an array of numbers.
 */
export function fromCssColor(hex: string): Color {
  const normalizedHex = hex.replace(/^#/, "");
  const r = parseInt(normalizedHex.substring(0, 2), 16) / 255;
  const g = parseInt(normalizedHex.substring(2, 4), 16) / 255;
  const b = parseInt(normalizedHex.substring(4, 6), 16) / 255;
  return [r, g, b, 1];
}

export function colorFromNumber(h: number): string {
  // omit grays and the first shade of every color
  const a = Math.abs(h);
  const hue = a % 6;
  const shade = (Math.floor(a / 6) % 3) + 1;
  return colorPalette[hue * 4 + shade].color;
}

export function isDarkColor(c: Color): boolean {
  return brightness(c) < 0.55;
}

export function isWhiteColor(c: Color): boolean {
  return brightness(c) > 0.9;
}

export function contrastCssColor(c: Color): string {
  return isDarkColor(c) ? color.back : color.textPrimary;
}

export function shadeColor(c: Color, amount = 0.25): Color {
  return isDarkColor(c) === amount > 0
    ? interpolate(c, white, Math.abs(amount))
    : interpolate(c, black, Math.abs(amount));
}

function brightness(c: Color): number {
  return c[0] * 0.299 + c[1] * 0.587 + c[2] * 0.114;
}

function interpolate(c: Color, d: Color, f: number): Color {
  return [c[0] + (d[0] - c[0]) * f, c[1] + (d[1] - c[1]) * f, c[2] + (d[2] - c[2]) * f, c[3]];
}

/**
 * HSL color representation.
 * - hue: 0-360
 * - saturation: 0-100
 * - lightness: 0-100
 */
export interface HSL {
  hue: number;
  saturation: number;
  lightness: number;
}

/**
 * Converts an HSL color object to a CSS hsla string.
 *
 * @param hsl - The HSL color object
 * @param alpha - Opacity 0-1 (default 1)
 * @returns CSS string like "hsla(240, 65%, 80%, 1)"
 */
export function hslToString(hsl: HSL, alpha = 1): string {
  return `hsla(${hsl.hue}, ${hsl.saturation}%, ${hsl.lightness}%, ${alpha})`;
}

/**
 * Converts a hex color (e.g. "#3ea009") to an HSL object.
 *
 * @param hex - The hex color code
 * @returns HSL with hue 0-360, saturation/lightness 0-100
 */
export function hexToHSL(hex: string): HSL {
  const [r, g, b] = fromCssColor(hex);
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const delta = max - min;
  const lightness = (max + min) / 2;

  let hue = 0;
  let saturation = 0;
  if (delta !== 0) {
    saturation = delta / (1 - Math.abs(2 * lightness - 1));
    if (max === r) hue = ((g - b) / delta) % 6;
    else if (max === g) hue = (b - r) / delta + 2;
    else hue = (r - g) / delta + 4;
    hue *= 60;
    if (hue < 0) hue += 360;
  }

  return {
    hue: Math.round(hue),
    saturation: Math.round(saturation * 100),
    lightness: Math.round(lightness * 100),
  };
}

/**
 * Softens a color while keeping its hue: scales saturation down and nudges lightness toward
 * white by a fraction of the remaining headroom. Proportional lightening keeps already-light
 * colors from washing out to pure white the way a fixed offset would.
 *
 * @param hsl - The HSL color to soften
 * @param saturationFactor - Multiplier for saturation (0-1), default 0.65
 * @param lightenFraction - Fraction of the distance to white to lighten (0-1), default 0.3
 * @returns New HSL object with adjusted values
 */
export function pastelHSL(hsl: HSL, saturationFactor = 0.65, lightenFraction = 0.3): HSL {
  return {
    hue: Math.max(0, Math.min(360, hsl.hue)),
    saturation: Math.max(0, Math.min(100, hsl.saturation * saturationFactor)),
    lightness: Math.max(0, Math.min(100, hsl.lightness + (100 - hsl.lightness) * lightenFraction)),
  };
}

/**
 * Mutes a color for supportive/non-primary elements — a mild desaturate plus a gentle
 * proportional lighten. Thin wrapper over {@link pastelHSL} with muting-specific factors.
 *
 * @param hsl - The HSL color to mute
 * @returns New HSL object with adjusted values
 */
export function mutedHSL(hsl: HSL): HSL {
  return pastelHSL(hsl, 0.8, 0.2);
}
